import React, { useState } from 'react'
import { ArrowLeft, Plus, RefreshCw } from 'lucide-react'
import { Badge } from '../components/Badge'
import { BuyerOrderEntry } from './BuyerOrderEntry'
import { RfqBuyerView, ViewQuote } from '../api/schema'
import { popLabel } from '../constants/pops'
import { getBuyerRFQ, awardQuote, declineRfqAsBuyer } from '../api'
import { useOpenBuyerRFQs } from '../hooks/useQuoteRequests'


// ─── Leg row (read-only — no provisioning form here) ─────────────────────────

function formatProtectionPeriod(rfq: RfqBuyerView): string {
  const frmt = (d: Date) => {
    return d.toLocaleDateString('en-SG', { timeZone: 'UTC', day: '2-digit', month: 'short', year: 'numeric' })
  }

  const from = new Date(rfq.protection_period_from)
  const until = new Date(rfq.protection_period_until)
  return `${frmt(from)} → ${frmt(until)} `
}

function total_quoted_price(rfq: RfqBuyerView): string {
  if(rfq.total_net_price) {
    // Total net price set (after a quote was awarded)
    return `$${parseFloat(rfq.total_net_price).toLocaleString()} USD`
  }

  if(rfq.quote_range_low && rfq.quote_range_high) {
    // Quote range set (after the RFQ was closed)
    if(rfq.quote_range_low === rfq.quote_range_high) {
    // If the range is singular, show just one price.
      return `$${parseFloat(rfq.quote_range_low).toLocaleString()} USD`
    }
    return `$${parseFloat(rfq.quote_range_low).toLocaleString()} - ${parseFloat(rfq.quote_range_high).toLocaleString()} USD`
  }

  return '—'
}

const FlatOrderRow: React.FC<{ rfq: RfqBuyerView; onClick: () => void }> = ({ rfq, onClick }) => (
  <tr style={{ borderBottom: '1px solid var(--color-border-tertiary)' }} onClick={onClick}
    onMouseEnter={(e: React.MouseEvent<HTMLTableRowElement>) => { e.currentTarget.style.background = 'var(--color-background-secondary)' }}
    onMouseLeave={(e: React.MouseEvent<HTMLTableRowElement>) => { e.currentTarget.style.background = 'transparent' }}>

    {/* RFQ ID */}
    <td style={{
      padding: '0.75rem', fontWeight: 500, fontSize: '0.875rem',
      color: 'var(--color-text-primary)'
    }}>
      {rfq.id}
    </td>

    {/* Protection Period */}
    <td style={{
      padding: '0.75rem', fontSize: '0.75rem',
      fontFamily: 'var(--font-mono)', color: 'var(--color-text-secondary)', whiteSpace: 'nowrap'
    }}>
      {formatProtectionPeriod(rfq)}
    </td>

    {/* Slots */}
    <td style={{
      padding: '0.75rem', fontSize: '0.75rem',
      fontFamily: 'var(--font-mono)', color: 'var(--color-text-secondary)', whiteSpace: 'nowrap'
    }}>
      {rfq.request_locations.length}
    </td>

    {/* Total Attack Surface */}
    <td style={{ padding: '0.75rem', fontSize: '0.8rem', color: 'var(--color-text-secondary)' }}>
      {rfq.request_locations.reduce((s, l) => s + l.dirty_bandwidth_gbps, 0)} Gbps
    </td>

    {/* Total Clean Bandwidth */}
    <td style={{ padding: '0.75rem', fontSize: '0.8rem', color: 'var(--color-text-secondary)' }}>
      {rfq.request_locations.reduce((s, l) => s + l.clean_bandwidth_gbps, 0)} Gbps
    </td>

    {/*   Total Quoted Price */}
    {<td style={{ padding: '0.75rem', fontSize: '0.8rem', color: 'var(--color-text-secondary)' }}>
      {total_quoted_price(rfq)}
    </td>}

    {/* Status */}
    <td style={{ padding: '0.75rem' }}>
      <Badge status={rfq.status} />
    </td>

    {/* Actions */}
    <td style={{ padding: '0.75rem' }}>
      <span style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)' }}>—</span>
    </td>
  </tr>
)

// RFQ details view

const RFQDetail: React.FC<{
  rfq: RfqBuyerView;
  onBack: () => void
  onAccept: (rfqId: string) => void
  onReject: (rfqId: string) => void
  onRefresh: () => Promise<void>
}> = ({ rfq, onBack, onAccept, onReject, onRefresh }) => {
  // Identifies which button is mid-request (e.g. 'reject' or 'accept:0').
  // Any non-null value disables all buttons; only the matching one shows "Processing…".
  const [pending, setPending] = useState<string | null>(null)
  const busy = pending !== null
  const [actionError, setActionError] = useState<string | null>(null)
  const [refreshing, setRefreshing] = useState(false)

  async function handleRefresh() {
    setRefreshing(true)
    try {
      await onRefresh()
    } finally {
      setRefreshing(false)
    }
  }

  async function handleAccept(rfqId: string, quote: ViewQuote, key: string) {
    setPending(key)
    setActionError(null)
    try {
      const choices = quote.locations.map(l => {
        return {
          location_id: l.location_id,
          seller_id: quote.seller_id as string,
        }
      })
      await awardQuote(rfqId, choices)
      onAccept(rfqId)
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Failed to accept')
    } finally {
      setPending(null)
    }
  }

  async function handleReject(rfqId: string) {
    setPending('reject')
    setActionError(null)
    try {
      await declineRfqAsBuyer(rfqId)
      onReject(rfqId)
    } catch (e) {
      setActionError(e instanceof Error ? e.message : 'Failed to reject')
    } finally {
      setPending(null)
    }
  }




  return (

    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

      {/* Back button + title */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
        <button onClick={onBack} style={{
          display: 'flex', alignItems: 'center', gap: '0.375rem',
          padding: '0.375rem 0.75rem', border: '1px solid var(--color-border-secondary)',
          borderRadius: '0.375rem', background: 'var(--color-background-primary)',
          fontSize: '0.75rem', color: 'var(--color-text-secondary)', cursor: 'pointer'
        }}>
          <ArrowLeft size={13} /> Back
        </button>
        <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 500, color: 'var(--color-text-primary)' }}>
          RFQ {rfq.id}
        </h2>
        <Badge status={rfq.status} />
        <button onClick={handleRefresh} disabled={refreshing} style={{
          display: 'flex', alignItems: 'center', gap: '0.375rem', marginLeft: 'auto',
          padding: '0.375rem 0.75rem', border: '1px solid var(--color-border-secondary)',
          borderRadius: '0.375rem', background: 'var(--color-background-primary)',
          fontSize: '0.75rem', color: 'var(--color-text-secondary)',
          cursor: refreshing ? 'not-allowed' : 'pointer'
        }}>
          <RefreshCw size={13} /> {refreshing ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {/* Summary cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '0.75rem' }}>
        {[
          { label: 'Protection Period', value: formatProtectionPeriod(rfq) },
          { label: 'Status', value: rfq.status },
        ].map(({ label, value }) => (
          <div key={label} style={{
            background: 'var(--color-background-primary)',
            border: '1px solid var(--color-border-secondary)',
            borderRadius: '0.5rem', padding: '0.75rem 1rem',
          }}>
            <div style={{
              fontSize: '0.68rem', color: 'var(--color-text-tertiary)',
              fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.04em',
              marginBottom: '0.25rem', width: '100%'
            }}>{label}</div>
            <div style={{
              fontSize: '0.875rem', fontWeight: 600,
              color: 'var(--color-text-primary)'
            }}>{value}</div>
          </div>
        ))}
      </div>

      {/* ── Accept / Reject bar — shown between summary and table ── */}
      {rfq.status === 'closed' && rfq.quotes.length > 0 && (
        <div style={{
          display: 'flex', gap: '0.75rem', alignItems: 'center',
          padding: '1rem 1.25rem',
          background: '#FFFBEB', border: '1px solid #FDE68A',
          borderRadius: '0.75rem',
        }}>
          <span style={{ fontSize: '0.875rem', color: '#92400E', fontWeight: 500, flex: 1 }}>
            This RFQ has received quotes and is awaiting your decision.
          </span>
          {actionError && (
            <span style={{ fontSize: '0.75rem', color: '#EF4444' }}>{actionError}</span>
          )}
          <button onClick={() => handleReject(rfq.id)} disabled={busy} style={{
            padding: '0.5rem 1.25rem', borderRadius: '0.375rem',
            border: '1px solid #EF4444', cursor: busy ? 'not-allowed' : 'pointer',
            background: '#EF4444', color: '#fff', opacity: busy && pending !== 'reject' ? 0.5 : 1,
            fontSize: '0.9rem', fontWeight: 600,
          }}>
            {pending === 'reject' ? 'Processing…' : 'Reject all'}
          </button>
        </div>

      )}

      {/* Quotes table — shown when closed */}
      {rfq.status === 'open' && (
        <div style={{ ...card, padding: 0 }}>
          <div style={{
            padding: '0.75rem 1rem', borderBottom: '1px solid var(--color-border-secondary)',
            fontSize: '0.875rem', fontWeight: 600, color: 'var(--color-text-primary)'
          }}>
            Requested Locations
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr style={{
                fontSize: '0.75rem', color: 'var(--color-text-secondary)',
                borderBottom: '1px solid var(--color-border-secondary)',
                background: 'var(--color-background-secondary)'
              }}>
                {['Location', 'Dirty Bandwidth', 'Clean Bandwidth'].map(h => (
                  <th key={h} style={{
                    padding: '0.75rem 1rem', textAlign: 'left',
                    fontWeight: 500, whiteSpace: 'nowrap'
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rfq.request_locations.map((loc, i) => (
                <tr key={i} style={{ borderBottom: '1px solid var(--color-border-tertiary)' }}>
                  <td style={{
                    padding: '0.75rem 1rem', fontSize: '0.8rem',
                    color: 'var(--color-text-primary)'
                  }}>
                    {popLabel(loc.location_id)}
                  </td>
                  <td style={{
                    padding: '0.75rem 1rem', fontSize: '0.8rem',
                    color: 'var(--color-text-secondary)'
                  }}>
                    {loc.dirty_bandwidth_gbps} Gbps
                  </td>
                  <td style={{
                    padding: '0.75rem 1rem', fontSize: '0.8rem',
                    color: 'var(--color-text-success)', fontWeight: 500
                  }}>
                    {loc.clean_bandwidth_gbps} Gbps
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}


      {/* Quotes table — shown when closed */}
      {rfq.status === 'closed' && rfq.quotes.length > 0 && (
        <div style={{ ...card, padding: 0 }}>
          <div style={{
            padding: '0.75rem 1rem', borderBottom: '1px solid var(--color-border-secondary)',
            fontSize: '0.875rem', fontWeight: 600, color: 'var(--color-text-primary)'
          }}>
            Quotes Received
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr style={{
                fontSize: '0.75rem', color: 'var(--color-text-secondary)',
                borderBottom: '1px solid var(--color-border-secondary)',
                background: 'var(--color-background-secondary)'
              }}>
                {['Seller', 'Location', 'Dirty Bandwidth', 'Dirty Bandwidth Price', 'Clean Bandwidth', 'Clean Bandwidth Price', 'Actions'].map(h => (
                  <th key={h} style={{
                    padding: '0.75rem 1rem', textAlign: 'left',
                    fontWeight: 500, whiteSpace: 'nowrap'
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rfq.quotes.map((q, qi) => {
                const isViewQuote = 'total_net_price' in q
                if (!isViewQuote) {
                  // Declined quote — show seller only
                  return (
                    <tr key={qi} style={{
                      borderBottom: '1px solid var(--color-border-tertiary)',
                      opacity: 0.5
                    }}>
                      <td style={{
                        padding: '0.75rem 1rem', fontSize: '0.8rem',
                        color: 'var(--color-text-secondary)'
                      }}>
                        {q.seller_name ?? '—'}
                      </td>
                      <td style={{
                        padding: '0.75rem 1rem', fontSize: '0.8rem',
                        color: 'var(--color-text-secondary)'
                      }}>
                        {q.seller_name ?? '—'}
                      </td>
                      <td colSpan={8} style={{
                        padding: '0.75rem 1rem', fontSize: '0.75rem',
                        color: 'var(--color-text-tertiary)'
                      }}>
                        Declined to quote
                      </td>
                    </tr>
                  )
                }
                const vq = q as ViewQuote
                return vq.locations.map((loc, li) => (
                  <tr key={`${qi}-${li}`}
                    style={{ borderBottom: '1px solid var(--color-border-tertiary)' }}>
                    {li === 0 && (
                      <td rowSpan={vq.locations.length} style={{
                        padding: '0.75rem 1rem', fontSize: '0.8rem',
                        fontWeight: 500, color: 'var(--color-text-primary)',
                        verticalAlign: 'top', borderRight: '1px solid var(--color-border-tertiary)',
                      }}>
                        {vq.seller_name ?? '—'}
                      </td>
                    )}
                    <td style={{
                      padding: '0.75rem 1rem', fontSize: '0.8rem',
                      color: 'var(--color-text-primary)'
                    }}>
                      {popLabel(loc.location_id)}
                    </td>
                    <td style={{
                      padding: '0.75rem 1rem', fontSize: '0.8rem',
                      color: 'var(--color-text-secondary)'
                    }}>
                      {loc.dirty_bandwidth_gbps} Gbps
                    </td>
                    <td style={{
                      padding: '0.75rem 1rem', fontSize: '0.8rem',
                      color: 'var(--color-text-secondary)'
                    }}>
                      ${parseFloat(loc.dirty_bandwidth_total_price).toLocaleString()} USD
                    </td>
                    <td style={{
                      padding: '0.75rem 1rem', fontSize: '0.8rem',
                      color: 'var(--color-text-success)', fontWeight: 500
                    }}>
                      {loc.clean_bandwidth_gbps} Gbps
                    </td>
                    <td style={{
                      padding: '0.75rem 1rem', fontSize: '0.8rem',
                      color: 'var(--color-text-secondary)'
                    }}>
                      ${parseFloat(loc.clean_bandwidth_total_price).toLocaleString()} USD
                    </td>

                    {li === 0 && (
                      <td rowSpan={vq.locations.length} style={{
                        padding: '0.75rem 1rem', fontSize: '0.8rem',
                        fontWeight: 500, color: 'var(--color-text-primary)',
                        verticalAlign: 'top', borderRight: '1px solid var(--color-border-tertiary)',
                      }}>

                        <button onClick={() => handleAccept(rfq.id, vq, `accept:${qi}`)} disabled={busy} style={{
                          padding: '0.5rem 1.25rem', borderRadius: '0.375rem',
                          border: 'none', cursor: busy ? 'not-allowed' : 'pointer',
                          background: pending === `accept:${qi}` ? '#94A3B8' : '#0D8F7A', color: '#fff',
                          opacity: busy && pending !== `accept:${qi}` ? 0.5 : 1,
                          fontSize: '0.9rem', fontWeight: 600,
                        }}>
                          {pending === `accept:${qi}` ? 'Processing…' : 'Accept'}
                        </button>
                      </td>
                    )}
                  </tr>
                ))
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Quotes table — shown when closed */}
      {(rfq.status === 'failed' || rfq.status === 'settled') && rfq.quotes.length > 0 && (
        <div style={{ ...card, padding: 0 }}>
          <div style={{
            padding: '0.75rem 1rem', borderBottom: '1px solid var(--color-border-secondary)',
            fontSize: '0.875rem', fontWeight: 600, color: 'var(--color-text-primary)'
          }}>
            RFQ
          </div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr style={{
                fontSize: '0.75rem', color: 'var(--color-text-secondary)',
                borderBottom: '1px solid var(--color-border-secondary)',
                background: 'var(--color-background-secondary)'
              }}>
                {['Seller', 'Location', 'Dirty Bandwidth', 'Dirty Bandwidth Price', 'Clean Bandwidth', 'Clean Bandwidth Price'].map(h => (
                  <th key={h} style={{
                    padding: '0.75rem 1rem', textAlign: 'left',
                    fontWeight: 500, whiteSpace: 'nowrap'
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rfq.quotes.map((q, qi) => {
                const isViewQuote = 'total_net_price' in q
                if (!isViewQuote) {
                  // Declined quote — show seller only
                  return (
                    <tr key={qi} style={{
                      borderBottom: '1px solid var(--color-border-tertiary)',
                      opacity: 0.5
                    }}>
                      <td style={{
                        padding: '0.625rem 0.75rem', fontSize: '0.8rem',
                        color: 'var(--color-text-secondary)'
                      }}>
                        {q.seller_name ?? '—'}
                      </td>
                      <td colSpan={8} style={{
                        padding: '0.625rem 0.75rem', fontSize: '0.75rem',
                        color: 'var(--color-text-tertiary)'
                      }}>
                        Declined to quote
                      </td>
                    </tr>
                  )
                }
                const vq = q as ViewQuote
                return vq.locations.map((loc, li) => (
                  <tr key={`${qi}-${li}`}
                    style={{ borderBottom: '1px solid var(--color-border-tertiary)' }}>
                    {li === 0 && (
                      <td rowSpan={vq.locations.length} style={{
                        padding: '0.625rem 0.75rem', fontSize: '0.8rem',
                        fontWeight: 500, color: 'var(--color-text-primary)',
                        verticalAlign: 'top', borderRight: '1px solid var(--color-border-tertiary)',
                      }}>
                        {vq.seller_name ?? '—'}
                      </td>
                    )}
                    <td style={{
                      padding: '0.625rem 0.75rem', fontSize: '0.8rem',
                      color: 'var(--color-text-primary)'
                    }}>
                      {popLabel(loc.location_id)}
                    </td>
                    <td style={{
                      padding: '0.625rem 0.75rem', fontSize: '0.8rem',
                      color: 'var(--color-text-secondary)'
                    }}>
                      {loc.dirty_bandwidth_gbps} Gbps
                    </td>
                    <td style={{
                      padding: '0.625rem 0.75rem', fontSize: '0.8rem',
                      color: 'var(--color-text-secondary)'
                    }}>
                      ${parseFloat(loc.dirty_bandwidth_total_price).toLocaleString()} USD
                    </td>
                    <td style={{
                      padding: '0.625rem 0.75rem', fontSize: '0.8rem',
                      color: 'var(--color-text-success)', fontWeight: 500
                    }}>
                      {loc.clean_bandwidth_gbps} Gbps
                    </td>
                    <td style={{
                      padding: '0.625rem 0.75rem', fontSize: '0.8rem',
                      color: 'var(--color-text-secondary)'
                    }}>
                      ${parseFloat(loc.clean_bandwidth_total_price).toLocaleString()} USD
                    </td>
                    {li === 0 && (
                      <>
                        {/* <td rowSpan={vq.locations.length} style={{
                    padding:'0.625rem 0.75rem', fontSize:'0.8rem',
                    color:'var(--color-text-secondary)', verticalAlign:'top',
                  }}>
                    ${parseFloat(vq.transaction_fees).toLocaleString()} USD
                  </td> */}
                        {/*   <td rowSpan={vq.locations.length} style={{
                    padding:'0.625rem 0.75rem', fontSize:'0.8rem',
                    color:'var(--color-text-primary)', fontWeight:600, verticalAlign:'top',
                  }}>
                    ${parseFloat(vq.total_gross_price).toLocaleString()} USD
                  </td> */}
                      </>
                    )}
                  </tr>
                ))
              })}
            </tbody>
          </table>
        </div>
      )}

    </div>
  )
}



// ─── Page ─────────────────────────────────────────────────────────────────────

export const BuyerInventory: React.FC = () => {
  const { rows: rfqs, loading, error, refresh } = useOpenBuyerRFQs()
  const [view, setView] = useState<'list' | 'new_rfq'>('list')
  const [selectedOrder, setSelectedOrder] = useState<RfqBuyerView | null>(null)

  function handleAccept(_orderId: string) {
    // TODO: replace with real API call
    setSelectedOrder(null)
    refresh()
  }

  function handleReject(_orderId: string) {
    // TODO: replace with real API call
    setSelectedOrder(null)
    refresh()
  }

  if (view === 'new_rfq') {
    return (
      <div>
        <button onClick={() => setView('new_rfq')} style={{
          display: 'flex', alignItems: 'center', gap: '0.375rem',
          marginBottom: '1rem', padding: '0.375rem 0.75rem',
          border: '1px solid var(--color-border-secondary)',
          borderRadius: '0.375rem', background: 'var(--color-background-primary)',
          fontSize: '0.75rem', color: 'var(--color-text-secondary)', cursor: 'pointer'
        }}>
          ← Back to RFQ listing
        </button>
        <BuyerOrderEntry />
      </div>
    )
  }

  if (selectedOrder) {
    return (
      <RFQDetail
        rfq={selectedOrder}
        onBack={() => setSelectedOrder(null)}
        onAccept={handleAccept}
        onReject={handleReject}
        onRefresh={async () => setSelectedOrder(await getBuyerRFQ(selectedOrder.id))}
      />
    )
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <h2 style={{ margin: 0, fontSize: '1rem', fontWeight: 500, color: 'var(--color-text-primary)' }}>
          RFQ Dashboard
        </h2>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={() => setView('new_rfq')} style={{
            display: 'flex', alignItems: 'center', gap: '0.375rem',
            padding: '0.375rem 0.75rem', border: 'none',
            borderRadius: '0.375rem', background: '#0D8F7A',
            fontSize: '0.75rem', color: '#fff', fontWeight: 600, cursor: 'pointer'
          }}>
            <Plus size={13} /> Start New RFQ
          </button>
          <button onClick={refresh} style={{
            display: 'flex', alignItems: 'center', gap: '0.375rem',
            padding: '0.375rem 0.75rem', border: '1px solid var(--color-border-secondary)',
            borderRadius: '0.375rem', background: 'var(--color-background-primary)',
            fontSize: '0.75rem', color: 'var(--color-text-secondary)', cursor: 'pointer'
          }}>
            <RefreshCw size={13} /> Refresh
          </button>
        </div>
      </div>


      {error && <p style={{ fontSize: '0.75rem', color: 'var(--color-text-danger)' }}>Error: {error}</p>}



      <div style={card}>
        {loading && <p style={{ fontSize: '0.75rem', color: 'var(--color-text-tertiary)', padding: '1.25rem' }}>Loading inventory…</p>}
        {!loading && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.875rem' }}>
            <thead>
              <tr style={{
                fontSize: '0.75rem', color: 'var(--color-text-secondary)',
                borderBottom: '1px solid var(--color-border-secondary)',
                background: 'var(--color-background-secondary)'
              }}>
                {['RFQ ID', 'Protection Period', 'Locations', 'Total Dirty Bandwidth', 'Total Clean Bandwidth', 'Total Quoted Price', 'Status', 'Actions'].map(h => (
                  <th key={h} style={{
                    padding: '0.625rem 0.75rem', textAlign: 'left',
                    fontWeight: 500, whiteSpace: 'nowrap'
                  }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rfqs.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{
                    padding: '2rem', textAlign: 'center',
                    fontSize: '0.75rem', color: 'var(--color-text-tertiary)'
                  }}>
                    No RFQs yet.
                  </td>
                </tr>
              ) : (
                rfqs.map(rfq => (
                  <FlatOrderRow key={rfq.id} rfq={rfq} onClick={() => setSelectedOrder(rfq)} />
                ))
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}


// ── Styles ────────────────────────────────────────────────────────────────────
const card: React.CSSProperties = {
  background: '#fff', borderRadius: '0.75rem',
  border: '1px solid #E2E8F0', padding: '1.25rem',
}
